import React, { FC, useState, useMemo, useEffect, useContext } from 'react';
import Box from '@pixel/core/Box';
import { ThemeProvider } from "@pixel/core";
import { createInternalTheme } from "@pixel/internal/styles";
import LayoutRoot from "@pixel/internal/LayoutRoot";
import FormInputLayout from '@pixel/core/FormInputLayout';
import { AutoSelect } from '@pixel/core';
import TextField from '@pixel/core/TextField';
import Button from '@pixel/core/Button';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import axios from 'axios';
import OdsContext from '../OdsContext';
import Link from '@pixel/core/Link';
import Autocomplete from '@mui/material/Autocomplete';
import FormLabel from '@pixel/core/FormLabel';
import StandardTooltip from '@pixel/core/StandardTooltip';
import InfoIcon from '@pixel/assets/icons/Info';
import Typography from "@pixel/core/Typography";
import { useSnackbar } from '@pixel/core/SnackbarService';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { IFetchAccessGroupPayload, ISaveAccessGroupPayload, IAccessGroupsData, IDimensionsAPIPayload, INamedDimensionData, IDimensionInfoMessage } from '../OdsTypes';
import CircularProgress from '@pixel/core/CircularProgress';
import FullWidthModalPagination from './FullWidthModalPagination';
import EmployeeAsyncSelect from "./EmployeeAysncSelect";
import AccessGroupAutoComplete from './AccessGroupAutoComplete';
import UsersList from "./UsersList";
import { overlayStyling, AddAccessModal } from './AccessGroupStyling';
import { AddDimensionModal } from './modal/AddDimensionModal';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import { getSelectedDimensionData, sortSelectedDimensions } from '../OdsTransform';
import ToolTip from '@pixel/core/Tooltip';
import { checkForAnyChangesInForm } from "../OdsTransform";

interface AddAccessGroupsDrawerProps {
  drawerState: boolean;
  setDrawerState: any;
  editAccessGroup: boolean;
  addAccessGroup: boolean;
  duplicateAccessGroup: boolean;
  accessDrawerData: IAccessGroupsData;
  setDuplicateAccessGroup: any;
  makeAccessGroupActive: any;
  makeAccessGroupInActive: any;
}

const AddAccessGroup: FC<AddAccessGroupsDrawerProps> = ({
  drawerState,
  setDrawerState,
  editAccessGroup,
  addAccessGroup,
  duplicateAccessGroup,
  accessDrawerData,
  setDuplicateAccessGroup,
  makeAccessGroupActive,
  makeAccessGroupInActive,
}) => {
  const {
    ODS_DUPLICATE_CAPTION,
    ODS_MAKE_ACTIVE_CAPTION,
    ODS_ACTIONS_MAKE_INACTIVE,
    ODS_LEAVE_FORM_CONFIRMATION_TITLE,
    ODS_LEAVE_FORM_CONFIRMATION_DESC,
    ACCESS_GROUP_SETTINGS,
    ODS_SAVE_BUTTON_CAPTION,
    ACCESS_GROUP_UPDATE,
    ODS_CANCEL_BUTTON_CAPTION,
    ACCESS_GROUP_EMPLOYEE,
    ACCESS_GROUP_DETAILS,
    COMMON_TO,
    COMMON_OPTIONAL,
    ODS_TAB_BUSINESS_UNITS,
    ODS_TAB_LOCATIONS,
    ODS_TAB_DEPARTMENTS,
    ACCESS_GROUP_NAME,
    ACCESS_GROUP_NAME_REQUIRED,
    ACCESS_GROUP_CODE_REQUIRED,
    ACCESS_GROUP_DUPLICATE_NAME_ERROR,
    ACCESS_GROUP_CODE,
    ACCESS_GROUP_DESCRIPTION,
    ACCESS_GROUP_EDIT_LOCATIONS,
    ACCESS_GROUP_EDIT_BUSINESS_UNITS,
    ACCESS_GROUP_EDIT_DEPARTMENTS,
    ACCESS_GROUP_ADDED_SUCCESSFULLY,
    ACCESS_GROUP_ADD_FAILED,
    ACCESS_GROUP_UPDATE_SUCCESSFULLY,
    ACCESS_GROUP_UPDATE_FAILED,
    ACCESS_GROUP_ADD_EMPLOYEES,
    ACCESS_GROUP_ADD_EMPLOYEES_INFO,
    ODS_REMOVE_EMPLOYEE_FROM_ACCESS_GROUP_CONFIRMATION_TITLE,
    ODS_REMOVE_EMPLOYEE_FROM_ACCESS_GROUP_CONFIRMATION_DESC,
    ACCESS_GROUP_DIMENSION_INFO_MESSAGE,
  } = useMultipleLocalizedText()

  const NO_OF_SELECTED_DIMENSIONS_TO_SHOW = 7;
  const snack = useSnackbar();
  const confirm = useConfirmation();
  const OrgElementsContext = useContext(OdsContext);
  const theme = createInternalTheme({ darkMode: false, theme: {} });
  // Dummy data will be removed once actual data is added

  const [selectedLocationForAPIPayload, setSelectedLocationForAPIPayload ] = useState<Array<IDimensionsAPIPayload>>([]);
  const [selectedDepartmentForAPIPayload, setSelectedDepartmentForAPIPayload] = useState<Array<IDimensionsAPIPayload>>([]);
  const [selectedBusinessUnitForAPIPayload, setSelectedBusinessUnitForAPIPayload] = useState<Array<IDimensionsAPIPayload>>([]);
  const [showInfoMessage, setShowInfoMessage] = useState<IDimensionInfoMessage>({
    locationInfoMessage: false,
    departmentInfoMessage: false,
    businessUnitInfoMessage: false,
  });

  const defaultAGPayload: ISaveAccessGroupPayload = useMemo(() => ({
    code: null,
    name: '',
    description: '',
    status: 'active',
    createdBy: window._jvselfUser.fullName,
    createdDate: new Date(),
    accessGroupUserSelectionList: [],
    accessGroupLocationSelectionList: selectedLocationForAPIPayload,
    accessGroupDepartmentSelectionList: selectedDepartmentForAPIPayload,
    accessGroupBusinessUnitSelectionList: selectedBusinessUnitForAPIPayload,
  }), []);

  const DIMENSION = {
    BUSINESS_UNIT: 'BUSINESS_UNIT',
    DEPARTMENT:'DEPARTMENT',
    LOCATION:'LOCATION',
  };

  const [addAccessGroupPayload, setAddAccessGroupPayload] = useState<ISaveAccessGroupPayload>(defaultAGPayload);
  const [formError, setFormError] = useState({ name: false, code: false });
  const [inProgressLoader, setInProgressLoader] = useState(false);
  const [addEmployees, setAddEmployees] = useState([]);
  const [usersListForRemoval, setUsersListForRemoval] = useState<Array<IDimensionsAPIPayload>>([]);
  const [showDuplicateErrorName, setShowDuplicateErrorName] = useState(false);
  const [openAddDimensionModal, setOpenAddDimensionModal] = useState(false);
  const [dimensionModalFor, setDimensionModalFor] = useState<string>(DIMENSION.BUSINESS_UNIT);
  const resetDrawerState = () => {
    setIsDimensionUpdated(false);
    setDrawerState(false);
    setFormError({ name: false, code: false });
    setAddAccessGroupPayload(defaultAGPayload);
    localStorage.removeItem('AG_location_list');
    localStorage.removeItem('AG_department_list');
    localStorage.removeItem('AG_businessUnit_list');
    localStorage.removeItem('namedLocationsData');
    localStorage.removeItem('namedDepartmentsData');
    localStorage.removeItem('namedBusinessUnitsData');
    setSelectedLocationForAPIPayload([]);
    setSelectedDepartmentForAPIPayload([]);
    setSelectedBusinessUnitForAPIPayload([]);
  }

  const [formState] = useState<ISaveAccessGroupPayload>(addAccessGroupPayload);
  const [formDrawerState] = useState<ISaveAccessGroupPayload>(accessDrawerData);
  const [isDimensionUpdated, setIsDimensionUpdated] = useState(false);
  const columnsToSkip = ['parentEId', 'actions', 'subLocationsCount', 'zip', 'eid', 'active', 'companyId', 'createdBy', 'createdDate', 'status', 'accessGroupLocationAssociation', 'accessGroupDepartmentAssociation', 'accessGroupBusinessUnitAssociation'];
  const handleAddAGDrawer = () => {
    const { 
      accessGroupLocationAssociation,
      accessGroupDepartmentAssociation,
      accessGroupBusinessUnitAssociation 
    } = accessDrawerData || {};

    const initailDrawerData = {
      ...accessDrawerData,
      accessGroupLocationSelectionList: accessGroupLocationAssociation?.associatedLocationList,
      accessGroupDepartmentSelectionList: accessGroupDepartmentAssociation?.associatedDepartmentList,
      accessGroupBusinessUnitSelectionList: accessGroupBusinessUnitAssociation?.associatedBusinessUnitList,
    };

    const accessGroupPayload = {
      ...addAccessGroupPayload,
      accessGroupLocationSelectionList: selectedLocationForAPIPayload,
      accessGroupDepartmentSelectionList: selectedDepartmentForAPIPayload,
      accessGroupBusinessUnitSelectionList: selectedBusinessUnitForAPIPayload,
    };

    const accessGroupEditPayload = {
      ...addAccessGroupPayload,
      accessGroupLocationSelectionList: isDimensionUpdated ? selectedLocationForAPIPayload : initailDrawerData.accessGroupLocationSelectionList,
      accessGroupDepartmentSelectionList: isDimensionUpdated ? selectedDepartmentForAPIPayload : initailDrawerData.accessGroupDepartmentSelectionList,
      accessGroupBusinessUnitSelectionList: isDimensionUpdated ? selectedBusinessUnitForAPIPayload : initailDrawerData.accessGroupBusinessUnitSelectionList,
    };

    const onLoadDrawerData = duplicateAccessGroup ? formDrawerState : formState;

    const isChanged = editAccessGroup 
      ? checkForAnyChangesInForm(accessGroupEditPayload, initailDrawerData, columnsToSkip)
      : checkForAnyChangesInForm(accessGroupPayload, onLoadDrawerData, columnsToSkip);

    if (!isChanged) {
      resetDrawerState();
    } else {
      confirm.open({
        dialogLabelSet: DialogLabelSet.CONFIRM,
        title: ODS_LEAVE_FORM_CONFIRMATION_TITLE,
        description: ODS_LEAVE_FORM_CONFIRMATION_DESC,
        onResolve: () => resetDrawerState(),
        onReject: (reason: string) => console.log(`callback rejected reason: ${reason}`),
      });
    }
  };

  const validateForm = () => {
    let temp: any = {};
    temp.name = addAccessGroupPayload.name === '' || addAccessGroupPayload.name === null;
    temp.code = addAccessGroupPayload.code === null;
    setFormError({
      ...temp
    });
    return Object.values(temp).every(x => x == '');
  }

  const checkUsersExistence = (user: any) => addAccessGroupPayload.accessGroupUserSelectionList.some( ({eid}) => eid == user.eid);

  useEffect(() => {
    addEmployees.forEach(function (value: any) {
      if (!checkUsersExistence(value)){
        setAddAccessGroupPayload({...addAccessGroupPayload, accessGroupUserSelectionList: [...addAccessGroupPayload.accessGroupUserSelectionList, {eid: value.eid, selected: true}]});
      } 
    });
  }, [addEmployees]);

  const reasonedAutoComplete = (newUsers:any, reason: string) => {
    if(reason == 'clear'){
      setAddEmployees([]);
      setAddAccessGroupPayload({...addAccessGroupPayload, accessGroupUserSelectionList: []});
    } else if(reason == 'removeOption'){
      const oddUser = addAccessGroupPayload.accessGroupUserSelectionList.filter(({ eid: firstEid }) => !newUsers.some(({ eid: secondEid }) => firstEid === secondEid));
      const filteredUsers = addAccessGroupPayload.accessGroupUserSelectionList.filter(object => {
        return object.eid !== oddUser[0].eid;
      });
      setAddAccessGroupPayload({...addAccessGroupPayload, accessGroupUserSelectionList: filteredUsers});
    }
  };

  const showError = (error: any) => {
    setInProgressLoader(false);
    if (error.response.data["Server_Error:"]) {
      snack.showError(error.response.data["Server_Error:"], OrgElementsContext.snackBarAutoHideTime);
    } else {
      snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
      if(error.response.data.errorMessage.search('name is already present') != -1){
        setFormError({ ...formError, name: true });
        setShowDuplicateErrorName(true);
      }
    }
  }

  const saveAGCallback = (response: any, updateMsg: string, failedMsg: string, accessGroupName: string) => {
    setInProgressLoader(false);
    if ([201, 200].includes(response.status)) {
      resetDrawerState();
      snack.showSuccess(`${accessGroupName} ${updateMsg}`, OrgElementsContext.snackBarAutoHideTime);
    } else {
      setInProgressLoader(false);
      snack.showError(failedMsg, OrgElementsContext.snackBarAutoHideTime);
    }
  }

  const showRemoveEmployeeConfirmDialog = () => {
    confirm.open({
      dialogLabelSet: DialogLabelSet.CONFIRM,
      title: ODS_REMOVE_EMPLOYEE_FROM_ACCESS_GROUP_CONFIRMATION_TITLE,
      description: ODS_REMOVE_EMPLOYEE_FROM_ACCESS_GROUP_CONFIRMATION_DESC,
      onResolve: (reason: string) => {
        updateAGSaveForm();
      },
      onReject: (reason: string) => {
        console.log('callback rejected reason: ' + reason);
        setInProgressLoader(false);
      }
    });
  }

  const onAddAGSaveForm = async () => {
    const accessGroupPayload = {
      ...addAccessGroupPayload,
      accessGroupLocationSelectionList: getSelectedDimensionData('AG_location_list'),
      accessGroupDepartmentSelectionList: getSelectedDimensionData('AG_department_list'),
      accessGroupBusinessUnitSelectionList: getSelectedDimensionData('AG_businessUnit_list'),
    };
    
    if (validateForm()) {
      setInProgressLoader(true);
      if(editAccessGroup && !duplicateAccessGroup){
        usersListForRemoval?.length > 0 ? showRemoveEmployeeConfirmDialog() : updateAGSaveForm();
      } else if (duplicateAccessGroup) {
        const duplicateAccessGroupPayload = {
          name: addAccessGroupPayload.name,
          description: addAccessGroupPayload.description,
          code: addAccessGroupPayload.code,
          createdBy: addAccessGroupPayload.createdBy,
          createdDate: addAccessGroupPayload.createdDate,
        }
        await axios
        .post(window.jvUrls.admin + '/action/accessGroups/duplicate/' + accessDrawerData.eid, duplicateAccessGroupPayload, {
          headers: {'Content-Type': 'application/json'}
        })
        .then(function (response) {
          saveAGCallback(response, ACCESS_GROUP_UPDATE_SUCCESSFULLY, ACCESS_GROUP_UPDATE_FAILED, duplicateAccessGroupPayload.name);
        })
        .catch(function (error) {
          showError(error);
        });
      } else {
        await axios
        .post(window.jvUrls.admin + '/action/accessGroups', accessGroupPayload, {
          headers: {'Content-Type': 'application/json; charset=UTF-8'}
        })
        .then(function (response) {
          saveAGCallback(response, ACCESS_GROUP_ADDED_SUCCESSFULLY, ACCESS_GROUP_ADD_FAILED, addAccessGroupPayload.name);
        })
        .catch(function (error) {
          showError(error);
        });
      }
    }
  }

  const updateAGSaveForm = async () => {
    const accessGroupPayload = {
      ...addAccessGroupPayload, 
      accessGroupLocationSelectionList: getSelectedDimensionData('AG_location_list'),
      accessGroupDepartmentSelectionList: getSelectedDimensionData('AG_department_list'),
      accessGroupBusinessUnitSelectionList: getSelectedDimensionData('AG_businessUnit_list'),
    }; // We can take this from constants

    await axios
        .put(window.jvUrls.admin + '/action/accessGroups/' + accessDrawerData.eid, accessGroupPayload, {
          headers: {'Content-Type': 'application/json'}
        })
        .then(function (response) {
          saveAGCallback(response, ACCESS_GROUP_UPDATE_SUCCESSFULLY, ACCESS_GROUP_UPDATE_FAILED, addAccessGroupPayload.name);
        })
        .catch(function (error) {
          showError(error);
        });
  }

  const addEmployee = () => {
    console.log(addEmployees);
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    let updatedPayload = { ...addAccessGroupPayload, [name]: value };
    if ((addAccessGroup || duplicateAccessGroup)) {
      if (name === 'name') {
        updatedPayload.code = value.replace(/[^\w\s]/gi, "").replace(/\s+/g, '_').toLowerCase();
      } else if (name === 'code') {
        updatedPayload.code = value;
      }
    }
    setShowDuplicateErrorName(false);
    setAddAccessGroupPayload(updatedPayload);
    setFormError({ ...formError, name: false });
  }

  const actionClickEvent = () => {
    if (accessDrawerData.actions.makeActive == 'visible') {
      makeAccessGroupActive(accessDrawerData);
    } else {
      makeAccessGroupInActive(accessDrawerData);
    }
  }

  const addLocations = () => {
    console.log('location');
  }

  useEffect(() => {
    if (editAccessGroup) {
      setAddAccessGroupPayload({
        ...addAccessGroupPayload,
        name: accessDrawerData.name,
        description: accessDrawerData.description,
        code: accessDrawerData.code,
        status: accessDrawerData.status,
        createdBy: window._jvselfUser.fullName,
        createdDate: new Date(),
        accessGroupLocationSelectionList: selectedLocationForAPIPayload
      });
    } else if (duplicateAccessGroup) {
        setAddAccessGroupPayload({
          ...addAccessGroupPayload,
          name: `[Copy] ${accessDrawerData.name}`,
          description: accessDrawerData.description,
          code: `copy_${accessDrawerData.code}`,
          status: accessDrawerData.status,
          createdBy: window._jvselfUser.fullName,
          createdDate: new Date(),
        });
    }
  }, []);

  const handleAddDimensionModalState = () => {
    setIsDimensionUpdated(true);
    setOpenAddDimensionModal(!openAddDimensionModal);
  }

  const handleAddBusinessUnits = () => {
    console.log('add / remove business units');
  }

  const handleAddDepartments = () => {
    console.log('add / remove departments');
  }

  const handleAddLocations = () => {
    console.log('add / remove locations');
  }

  const handleAddSelectedDimension = {
    BUSINESS_UNIT: () => handleAddBusinessUnits(),
    DEPARTMENT: () => handleAddDepartments(),
    LOCATION: () => handleAddLocations(),
  };

  const renderDimensionChip = (chip: INamedDimensionData) => {
    const label = chip?.name;
    if (label?.length > 10) {
      const truncatedLabel = label?.length > 10 ? `${label?.substring(0, 10)}...` : label;
      return <ToolTip title={label} key={chip?.eid}>
        <Chip key={chip?.eid} label={truncatedLabel} style={{ margin:'4px'}}/>
      </ToolTip>
    } else {
      return <Chip key={chip?.eid} label={label} style={{ margin:'4px'}}/>
    }
  }

  const renderInfoMessage = (showInfoMessage: boolean) => {
    return (
        showInfoMessage ? (
          <Typography style={{ color: "#313638", fontSize: "11px" }}>
            {ACCESS_GROUP_DIMENSION_INFO_MESSAGE}
          </Typography>
        ) : null
    );
  }

  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <LayoutRoot className='add-edit-access-group-wrapper'>
          <FullWidthModalPagination
            labelDrawerForm={editAccessGroup ? accessDrawerData.name : ACCESS_GROUP_SETTINGS}
            labelSaveDrawerForm={editAccessGroup && !duplicateAccessGroup ? ACCESS_GROUP_UPDATE : ODS_SAVE_BUTTON_CAPTION}
            labelClose={ODS_CANCEL_BUTTON_CAPTION}
            anchor='right'
            drawerWidth='80%'
            onSaveForm={onAddAGSaveForm}
            onCancel={handleAddAGDrawer}
            ListPaginatorResults={false}
            open={drawerState}
            DrawerActionButtonProps={editAccessGroup && {
              className: 'drawer-action-button',
              disabled: accessDrawerData.actions.makeInactive == 'grayout',
              actionLabel: accessDrawerData.actions.makeActive == 'visible' ? ODS_MAKE_ACTIVE_CAPTION : ODS_ACTIONS_MAKE_INACTIVE,
              actionClick: actionClickEvent,
            }}
          >
            {(inProgressLoader && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
            <Box sx={AddAccessModal} className={'add-access-modal-container'}>
              <AddDimensionModal
                modalState={openAddDimensionModal}
                setAddDimensionModalState={handleAddDimensionModalState}
                addSelectedDimensions={handleAddSelectedDimension[dimensionModalFor]}
                dimension={dimensionModalFor}
                accessDrawerData={accessDrawerData}
                selectedLocationForAPIPayload={selectedLocationForAPIPayload}
                setSelectedLocationForAPIPayload={setSelectedLocationForAPIPayload}
                selectedDepartmentForAPIPayload={selectedDepartmentForAPIPayload}
                setSelectedDepartmentForAPIPayload={setSelectedDepartmentForAPIPayload}
                selectedBusinessUnitForAPIPayload={selectedBusinessUnitForAPIPayload}
                setSelectedBusinessUnitForAPIPayload={setSelectedBusinessUnitForAPIPayload}
                showInfoMessage={showInfoMessage}
                setShowInfoMessage={setShowInfoMessage}
              />
              
              {!duplicateAccessGroup && (
                <Box className={'add-access-modal-left'}>
                  <Box className={'modal-sub-header'}>
                    <Typography variant="h3" noWrap>{ACCESS_GROUP_EMPLOYEE}</Typography>
                    <Box className={'add-employee-wrapper'}>
                      <FormLabel className='add-employee-label' >{ACCESS_GROUP_ADD_EMPLOYEES}
                        <StandardTooltip title={ACCESS_GROUP_ADD_EMPLOYEES_INFO}>
                          <InfoIcon />
                        </StandardTooltip>
                      </FormLabel>
                      <AccessGroupAutoComplete
                        value={addEmployees}
                        isMakeInactiveGrayout = {accessDrawerData == null ? false : (accessDrawerData.actions) == null ? false : (accessDrawerData.actions.makeInactive == 'grayout')}
                        accessGroupEid={accessDrawerData ? accessDrawerData.eid : ''}
                        onChange={(event, newUsers: any, reason) => {
                          setAddEmployees(newUsers);
                          reasonedAutoComplete(newUsers, reason);
                        }}
                      />
                    </Box>
                  </Box>
                  <Box className={'modal-list-wrapper'}>
                    <UsersList
                      selectedUserData={accessDrawerData}
                      usersListForRemoval={usersListForRemoval} 
                      setUsersListForRemoval={setUsersListForRemoval}
                      addEmployees={addEmployees}
                      setAddEmployees={setAddEmployees}
                      setAddAccessGroupPayload={setAddAccessGroupPayload}
                      addAccessGroupPayload={addAccessGroupPayload}
                    />
                  </Box>
                </Box>
              )}
    
              <Box className={'add-access-modal-right'}>
                <Typography variant="h3" noWrap>{ACCESS_GROUP_DETAILS}</Typography>
                <FormInputLayout>
                  <Box className={'access-group-name'}><TextField label={ACCESS_GROUP_NAME} maxLength={200} error={formError.name} errorText={showDuplicateErrorName ? ACCESS_GROUP_DUPLICATE_NAME_ERROR : ACCESS_GROUP_NAME_REQUIRED} name='name' onChange={handleInputChange} value={addAccessGroupPayload.name} fullWidth required></TextField></Box>
                </FormInputLayout>
                <FormInputLayout>
                  <Box className={'access-group-code'}><TextField label={ACCESS_GROUP_CODE} maxLength={200} error={formError.code} errorText={showDuplicateErrorName ? ACCESS_GROUP_DUPLICATE_NAME_ERROR : ACCESS_GROUP_CODE_REQUIRED} name='code' onChange={handleInputChange} value={addAccessGroupPayload.code} readOnly={editAccessGroup} fullWidth required ></TextField></Box>
                </FormInputLayout>
                <FormInputLayout>
                  <Box className={'access-group-description'}><TextField onChange={handleInputChange} name='description' value={addAccessGroupPayload.description} label={ACCESS_GROUP_DESCRIPTION} multiline fullWidth ></TextField></Box>
                </FormInputLayout>

                {!duplicateAccessGroup && (
                  <>
                    <Box className={'access-group-BU'}>
                      <InputLabel className={'access-group-dimension-label'}>Business Unit</InputLabel>
                      <Box className={'access-group-dimension-input'}
                        onClick={() => {
                          setOpenAddDimensionModal(!openAddDimensionModal);
                          setDimensionModalFor(DIMENSION.BUSINESS_UNIT);
                        }}>
                          {
                            sortSelectedDimensions("AG_businessUnit_list", 'namedBusinessUnitsData')?.map((chip: INamedDimensionData, index: number) =>  index < NO_OF_SELECTED_DIMENSIONS_TO_SHOW && renderDimensionChip(chip))                          
                          }
                      </Box>
                      <Box>
                        {renderInfoMessage(showInfoMessage?.businessUnitInfoMessage)}
                      </Box>
                      <Link underline="hover" sx={{ maxWidth: '160px', fontWeight: '600' }} onClick={() => {
                        setOpenAddDimensionModal(!openAddDimensionModal);
                        setDimensionModalFor(DIMENSION.BUSINESS_UNIT);
                      }}
                      variant="body2">{ACCESS_GROUP_EDIT_BUSINESS_UNITS}</Link>
                    </Box>
                    <Box className={'access-group-location'}>
                    <InputLabel className={'access-group-dimension-label'}>Locations</InputLabel>
                    <Box className={'access-group-dimension-input'} 
                      onClick={() => {
                        setOpenAddDimensionModal(!openAddDimensionModal);
                        setDimensionModalFor(DIMENSION.LOCATION);
                      }}>
                        {
                          sortSelectedDimensions("AG_location_list", 'namedLocationsData')?.map((chip: INamedDimensionData, index: number) =>  index < NO_OF_SELECTED_DIMENSIONS_TO_SHOW && renderDimensionChip(chip))
                        }
                    </Box>
                    <Box>
                        {renderInfoMessage(showInfoMessage?.locationInfoMessage)}
                    </Box>
                    <Link underline="hover" sx={{ maxWidth: '160px', fontWeight: '600' }} onClick={() => {
                      setOpenAddDimensionModal(!openAddDimensionModal);
                      setDimensionModalFor(DIMENSION.LOCATION);
                    }}
                    variant="body2">{ACCESS_GROUP_EDIT_LOCATIONS}</Link>
                    </Box>
                    <Box className={'access-group-department'}>
                    <InputLabel className={'access-group-dimension-label'}>Departments</InputLabel>
                    <Box className={'access-group-dimension-input'} 
                      onClick={() => {
                        setOpenAddDimensionModal(!openAddDimensionModal);
                        setDimensionModalFor(DIMENSION.DEPARTMENT);
                      }}>
                        {
                          sortSelectedDimensions("AG_department_list", 'namedDepartmentsData')?.map((chip: INamedDimensionData, index: number) =>  index < NO_OF_SELECTED_DIMENSIONS_TO_SHOW && renderDimensionChip(chip))
                        }
                    </Box>
                    <Box>
                        {renderInfoMessage(showInfoMessage?.departmentInfoMessage)}
                    </Box>
                    <Link underline="hover" sx={{ maxWidth: '160px', fontWeight: '600' }} onClick={() => {
                      setOpenAddDimensionModal(!openAddDimensionModal);
                      setDimensionModalFor(DIMENSION.DEPARTMENT);
                    }}
                    variant="body2">{ACCESS_GROUP_EDIT_DEPARTMENTS}</Link>
                    </Box>
                  </>
                )}

              </Box>
            </Box>
          </FullWidthModalPagination>
        </LayoutRoot>
      </ThemeProvider>
    </React.StrictMode>
  );
};

export default AddAccessGroup;